// API utility functions for different AI models
import { API_CONFIG } from "../config/api";
import { supabase } from "@/integrations/supabase/client";
import { MessageAttachment, MessageSource, ConversationFeatures } from "@/hooks/tutor/types";
import { CHAT_CONNECTION_TIMEOUT_MS, CHAT_STREAM_STALL_TIMEOUT_MS } from "./chatTimeouts";

// Helper for delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Backend API with streaming support
export const callLocalBackend = async (
  messages: Array<{ role: string; content: string; attachments?: MessageAttachment[] }>,
  provider: "openai" | "anthropic" | "gemini",
  setPartialResponse: (text: string) => void,
  setToolActivity: (activity: { tool: string; count?: number } | null) => void,
  onImageGenerated?: (imageUrl: string) => void,
  onUsageUpdate?: (usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  }) => void,
  modelId?: string,
  conversationId?: string,
  processMindmap?: boolean,
  enabledFeatures?: ConversationFeatures,
  onSourcesReceived?: (sources: MessageSource[]) => void,
  clientLanguage?: string
): Promise<string> => {
  let lastError: unknown;
  const maxRetries = 3;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // One AbortController per attempt so timeouts only affect the current try.
    // Connection timeout fires if fetch hasn't returned its first byte in time;
    // stall timeout fires if no new SSE chunk arrives for too long. Both are
    // cleared on success and converted into throws so the retry loop picks up.
    const abortController = new AbortController();
    let connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let stallTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearConnectionTimer = () => {
      if (connectionTimeoutId !== null) {
        clearTimeout(connectionTimeoutId);
        connectionTimeoutId = null;
      }
    };
    const clearStallTimer = () => {
      if (stallTimeoutId !== null) {
        clearTimeout(stallTimeoutId);
        stallTimeoutId = null;
      }
    };
    const armStallTimer = () => {
      clearStallTimer();
      stallTimeoutId = setTimeout(() => {
        console.error(
          `[chat] Stream stalled (no chunk for ${CHAT_STREAM_STALL_TIMEOUT_MS}ms), aborting`
        );
        abortController.abort(new Error("CHAT_STREAM_STALLED"));
      }, CHAT_STREAM_STALL_TIMEOUT_MS);
    };

    try {
      console.log(
        `Calling backend API with provider: ${provider} (Attempt ${attempt + 1}/${maxRetries})`
      );
      console.log(`Using API endpoint: ${API_CONFIG.CHAT_API_URL}`);

      connectionTimeoutId = setTimeout(() => {
        console.error(`[chat] Connection timeout (${CHAT_CONNECTION_TIMEOUT_MS}ms), aborting`);
        abortController.abort(new Error("CHAT_CONNECTION_TIMEOUT"));
      }, CHAT_CONNECTION_TIMEOUT_MS);

      const reader = await getReader(
        messages,
        provider,
        modelId,
        conversationId,
        processMindmap,
        enabledFeatures,
        clientLanguage,
        abortController.signal
      );

      // Response headers received — connection is alive, switch to stall watchdog.
      clearConnectionTimer();
      armStallTimer();

      let fullResponse = "";
      const decoder = new TextDecoder();
      let buffer = "";
      // Set once the server signals end-of-stream via `data.done`. We use this
      // to exit the outer read loop immediately instead of waiting for the
      // stream to close, which prevents the stall watchdog from aborting a
      // response that's logically complete but whose underlying stream the
      // server is slow to close.
      let streamDone = false;

      // Process the stream
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        // Activity on the stream — reset stall watchdog.
        armStallTimer();

        // Convert the Uint8Array to a string with stream: true to handle multibyte characters correctly
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Handle SSE format - split by double newline
        // Keep the last part in the buffer as it might be incomplete
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.replace("data: ", "");
              const data = JSON.parse(jsonStr);

              if (data.toolUse) {
                setToolActivity(data.toolUse);
              }

              if (data.imageGenerated) {
                // Handle generated image event
                if (onImageGenerated) {
                  onImageGenerated(data.imageGenerated.url);
                }
              }

              if (data.sources && onSourcesReceived) {
                // Handle search sources for UI display
                console.log("🔗 [aiModels] Received sources from stream:", data.sources.length);
                onSourcesReceived(data.sources);
              }

              if (data.content) {
                // Clear tool activity when content starts streaming
                setToolActivity(null);
                fullResponse += data.content;
                setPartialResponse(fullResponse);
              }

              if (data.usage && onUsageUpdate) {
                // Extract and report usage information
                console.log("📊 [aiModels] Received usage from stream:", data.usage);
                // Pass total_tokens to updateUsage
                onUsageUpdate(data.usage);
              }

              if (data.done) {
                // Stream is complete - ensure tool activity is cleared
                setToolActivity(null);
                // Check if usage was included in the done event
                if (data.usage && onUsageUpdate) {
                  console.log("📊 [aiModels] Usage in done event:", data.usage);
                  // Pass total_tokens to updateUsage
                  onUsageUpdate(data.usage);
                }
                streamDone = true;
                break;
              }
            } catch (e) {
              console.error("Error parsing SSE chunk:", e);
            }
          }
        }
      }

      clearStallTimer();
      return fullResponse;
    } catch (error) {
      console.error(`Error in backend call (Attempt ${attempt + 1}/${maxRetries}):`, error);
      lastError = error;

      // Check if error is a 4xx client error (except 429) - if so, don't retry
      if (error instanceof Error && error.message.includes("API responded with status: 4")) {
        // Retry on 429 (Too Many Requests) or 408 (Request Timeout)
        if (!error.message.includes("429") && !error.message.includes("408")) {
          throw error;
        }
      }

      // If we have retries left, reset state and wait
      if (attempt < maxRetries - 1) {
        console.log(`Retrying in ${(attempt + 1) * 1000}ms...`);
        setPartialResponse(""); // Clear partial response to avoid appending garbage
        setToolActivity(null);
        await delay(1000 * (attempt + 1)); // Exponential backoff-ish (1s, 2s, 3s)
      }
    } finally {
      clearConnectionTimer();
      clearStallTimer();
    }
  }

  throw lastError;
};

export const getAccessToken = async (): Promise<string> => {
  const { data: tokenData, error: tokenError } = await supabase.auth.getSession();
  if (tokenError) {
    console.error("Failed to get authentication token:", tokenError);
    throw new Error("Failed to get authentication token");
  }

  const accessToken = tokenData.session?.access_token;
  if (!accessToken) {
    console.error("Missing access token in session data");
    throw new Error("Missing access token");
  }
  return accessToken;
};

const getReader = async (
  messages: Array<{ role: string; content: string; attachments?: MessageAttachment[] }>,
  provider: "openai" | "anthropic" | "gemini",
  modelId?: string,
  conversationId?: string,
  processMindmap?: boolean,
  enabledFeatures?: ConversationFeatures,
  clientLanguage?: string,
  signal?: AbortSignal
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
  const accessToken = await getAccessToken();

  const payload = {
    messages: messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
      attachments: msg.attachments,
    })),
    provider: provider,
    modelId: modelId,
    conversationId: conversationId,
    processMindmap: processMindmap || false,
    enabledFeatures: enabledFeatures || {},
    clientLanguage: clientLanguage,
  };

  console.log(
    "🖼️ Sending to backend, messages with attachments:",
    JSON.stringify(
      payload.messages.map((m) => ({
        role: m.role,
        contentLength: m.content?.length || 0,
        hasAttachments: !!m.attachments,
        attachmentCount: m.attachments?.length || 0,
        attachments: m.attachments,
      })),
      null,
      2
    )
  );

  const response = await fetch(API_CONFIG.CHAT_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`API error (${response.status}):`, errorText);
    throw new Error(`API responded with status: ${response.status} - ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Failed to get response reader");
  }

  return reader;
};
